import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import gsap from "gsap";
import SeamlessLoopVideo from "./SeamlessLoopVideo";
import Seo from "./components/Seo";
import FastScrollReveal from "./components/FastScrollReveal";

// Icons - import only once from each source
import {
  ArrowRight,
  Lightbulb,
  Gauge,
  Users,
  Globe2,
  CheckCircle,
  Building2,
  Package,
  UserCheck,
  Grid3x3,
  MessageCircle,
  Clock,
  Plane,
  Cpu,
  Leaf,
  Layers,
  Pill,
  TestTube,
  DollarSign,
  GraduationCap,
  Trophy,
  ShoppingCart,
  Factory

} from "lucide-react";
import { ArrowLeft } from "lucide-react";

// Import all your images
import Img1 from '../src/Images/Homevideo.mp4';
import Img2 from '../src/Images/img2.webp';
import blogsimg from '../src/Images/blogsimg.webp';
import casestudyImg from '../src/Images/casestudy.webp';
import { responsiveImg, imgErrorFallback } from "./utils/img";
import Service1 from '../src/Images/Service1.webp';
import Service2 from '../src/Images/Service2.webp';
import Service3 from '../src/Images/Service3.webp';
import Service4 from '../src/Images/Service4.webp';
import Service5 from '../src/Images/Service5.webp';
import Service6 from '../src/Images/Service6.webp';
import Service7 from '../src/Images/Service7.webp';
import Service8 from '../src/Images/Service8.webp';
import Service9 from '../src/Images/Service9.webp';
import Service10 from '../src/Images/Service10.webp';
// import Acadmea from '../src/Images/Acadmea_img.webp';
// "Our In-House Products" cards and the "Empowering Success Stories" client
// logos are now CMS-managed: fetched from the backend instead of the static
// imports that used to live here. See productAPI / clientAPI below.
import { productAPI, clientAPI, categoryAPI, statAPI, heroAPI, blogAPI, caseStudyAPI } from "./services/api";
import { isMovedToAISolutions } from "./utils/aiSolutionMoves";
import India from '../src/Images/India.webp';
import Canada from '../src/Images/Canada.webp';
import Singapore from '../src/Images/Singapore.webp';
import Australia from '../src/Images/Australia.webp';
import USA from '../src/Images/USA.webp';
import Europe from '../src/Images/Europe.webp';
import SK from '../src/Images/SK.webp';
import UK from '../src/Images/UK.webp';
import Dubai from '../src/Images/Dubai.webp';
import PredoImg from '../src/Images/PredoSolutionThumbnail.webp';
import AskO from '../src/Images/AskOchatThumbnail.webp';
import Zentick from '../src/Images/ZenetickAiThumbnail.webp';
import BestoFlowImg from '../src/Images/BestoFlowThumbnail.webp';
import Besto_Jyotish from '../src/Images/BestoJyotishThumbnail.webp';
import '../src/App.css';
import { useNavigate } from "react-router-dom";
import { serviceTabPath } from "./utils/serviceTabs";

// Maps a CMS-stored icon NAME (lucide-react) to the imported component for the
// "Our Track Record So Far" stats. Unknown names fall back to Grid3x3. Only
// icons already imported above are included (keeps the eager Home bundle lean).
const STAT_ICON_MAP = {
  Clock, Globe2, CheckCircle, Users, UserCheck, Grid3x3, Trophy, Package,
  Building2, Cpu, Factory, GraduationCap, DollarSign, ShoppingCart, Layers,
  Gauge, Lightbulb, MessageCircle, Plane, Leaf, Pill, TestTube,
};

// Shared scroll-reveal trigger for every `reveal()` block: fire as soon as any
// part of the block is within 15% of a screen height below the viewport. The
// old `amount: 0.2` waited until a fifth of the block was ON screen, which left
// a blank band at the bottom of the screen (up to ~0.9s on tall blocks).
const REVEAL_VIEWPORT = { once: true, amount: "some", margin: "0px 0px 15% 0px" };

// Router <Link> with framer-motion hover/tap props (AI tool "Read More").
const MotionLink = motion.create(Link);

// A product's media can be a YouTube video instead of an uploaded image. The
// product card renders a still <img>, so derive the video's thumbnail from its
// URL. Accepts any YouTube form (watch / youtu.be / embed / shorts / bare ID) —
// the same parse the Products page uses. Returns null when there's no video.
const youTubeThumb = (input) => {
  if (!input) return null;
  const s = String(input).trim();
  const id = /^[a-zA-Z0-9_-]{11}$/.test(s)
    ? s
    : (s.match(
        /(?:youtu\.be\/|youtube(?:-nocookie)?\.com\/(?:watch\?v=|embed\/|shorts\/|v\/)|[?&]v=)([a-zA-Z0-9_-]{11})/
      ) || [])[1];
  // hqdefault is 480x360 — enough for the 348px-wide card and always present
  // (maxresdefault 404s on plenty of videos).
  return id ? `https://i.ytimg.com/vi/${id}/hqdefault.jpg` : null;
};

// Home hero copy shown instantly on first paint (avoids CLS) and used if the
// hero API is unreachable. Mirrors what `npm run seed:hero` writes to the DB.
const FALLBACK_HERO = {
  headingLine1: "Where Strategy Meets Scalable",
  headingLine2: "Digital Innovation",
  subheading:
    "From SAP Implementation and Industry 4.0 / 5.0 to AI-powered platforms to Custom Web Application — we deliver scalable, future-ready technology that drives growth and innovation.",
  ctaText: "Explore Our Capabilities",
  ctaLink: "/bestoservice",
};

// Shown instantly on first paint (avoids CLS) and used if the stats API is
// unreachable. Mirrors what `npm run seed:stats` writes to the DB.
const FALLBACK_STATS = [
  { number: "20+", label: "Years of Industry Experience", icon: "Clock" },
  { number: "74+", label: "Clients Served Worldwide", icon: "Globe2" },
  { number: "18+", label: "SAP IBP Projects Delivered", icon: "CheckCircle" },
  { number: "43+", label: "SAP IBP Experts Onboard", icon: "Users" },
  { number: "1000+", label: "FTEs & subcontractors deployed", icon: "UserCheck" },
  { number: "14+", label: "Products and Apps", icon: "Grid3x3" },
];

const Hero = () => {

  // AI Tools scroll state
  const aiScrollRef = useRef(null);
  const [canScrollLeftAI, setCanScrollLeftAI] = useState(false);
  const [canScrollRightAI, setCanScrollRightAI] = useState(true);
  // Horizontal scroll for the "Our In-House Products" row.
  const productsScrollRef = useRef(null);
  const [canScrollLeftProd, setCanScrollLeftProd] = useState(false);
  const [canScrollRightProd, setCanScrollRightProd] = useState(false);
  const navigate = useNavigate();

  // ---- Hero entrance animation (GSAP) ----
  // The heading/description/CTA use their original framer-motion animation
  // (see the hero markup below); GSAP only handles the background video and
  // dark-overlay fade-in here.
  const heroRef = useRef(null);
  const heroVideoRef = useRef(null);
  const heroOverlayRef = useRef(null);

  // Plays once on mount. Animates only opacity (no layout shift), respects
  // prefers-reduced-motion, and cleans up via gsap.context().revert().
  useLayoutEffect(() => {
    const prefersReduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    if (prefersReduced) {
      // Ensure everything is visible at its final state without animating.
      gsap.set([heroVideoRef.current, heroOverlayRef.current], {
        clearProps: "all",
      });
      return;
    }

    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: "power2.out" } });

      // 1. Background video — fade 0 -> 1 over 900ms (no transform), at 0ms
      tl.fromTo(
        heroVideoRef.current,
        { opacity: 0 },
        { opacity: 1, duration: 0.9 },
        0
      );

      // 2. Dark overlay — fade in (500ms) for readability, at 100ms
      tl.fromTo(
        heroOverlayRef.current,
        { opacity: 0 },
        { opacity: 1, duration: 0.5 },
        0.1
      );
    }, heroRef);

    return () => ctx.revert();
  }, []);

  // Check scroll position for AI Tools section
  const checkScrollPositionAI = () => {
    if (aiScrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = aiScrollRef.current;
      const canLeft = scrollLeft > 0;
      const canRight = scrollLeft < scrollWidth - clientWidth - 10;

      setCanScrollLeftAI(prev => prev !== canLeft ? canLeft : prev);
      setCanScrollRightAI(prev => prev !== canRight ? canRight : prev);
    }
  };

  useEffect(() => {
    checkScrollPositionAI();
    window.addEventListener('resize', checkScrollPositionAI);
    return () => window.removeEventListener('resize', checkScrollPositionAI);
  }, []);

  const scrollAI = (direction) => {
    if (!aiScrollRef.current) return;

    const container = aiScrollRef.current;
    const cardWidth = container.querySelector('.ai-tool-card')?.offsetWidth || 400;
    const gap = 24;
    const scrollAmount = cardWidth + gap;

    if (direction === "left") {
      container.scrollBy({ left: -scrollAmount, behavior: "smooth" });
    } else {
      container.scrollBy({ left: scrollAmount, behavior: "smooth" });
    }

    setTimeout(checkScrollPositionAI, 400);
  };

  // Horizontal scroll handlers for the In-House Products row.
  const checkProductsScroll = () => {
    if (productsScrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = productsScrollRef.current;
      const canLeft = scrollLeft > 0;
      const canRight = scrollLeft < scrollWidth - clientWidth - 10;
      setCanScrollLeftProd((p) => (p !== canLeft ? canLeft : p));
      setCanScrollRightProd((p) => (p !== canRight ? canRight : p));
    }
  };

  const scrollProducts = (direction) => {
    if (!productsScrollRef.current) return;
    const container = productsScrollRef.current;
    const cardWidth = container.querySelector(".inhouse-card")?.offsetWidth || 340;
    const amount = cardWidth + 28;
    container.scrollBy({ left: direction === "left" ? -amount : amount, behavior: "smooth" });
    setTimeout(checkProductsScroll, 400);
  };

  // Scroll-reveal animations are a DESKTOP-ONLY enhancement. On touch / small
  // screens, fast scrolling can leave framer-motion `whileInView` blocks stuck
  // hidden — the IntersectionObserver never registers the threshold, so the
  // section stays at opacity 0 forever. Below the desktop breakpoint (and for
  // users who prefer reduced motion) we drop the reveal props entirely so each
  // block renders at its natural, visible state immediately — matching the
  // site's ScrollReveal / `.reveal-item` convention. Reactive to crossing the
  // 1024px boundary (e.g. device-mode / window resize).
  const [revealOn, setRevealOn] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(min-width: 1024px)").matches &&
      !window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
  useEffect(() => {
    const wide = window.matchMedia("(min-width: 1024px)");
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setRevealOn(wide.matches && !reduce.matches);
    wide.addEventListener("change", update);
    reduce.addEventListener("change", update);
    return () => {
      wide.removeEventListener("change", update);
      reduce.removeEventListener("change", update);
    };
  }, []);
  // Spread onto a motion element: returns the reveal props on desktop, or {} on
  // mobile/reduced-motion so the element (and any stagger children) shows at
  // once. Every block shares REVEAL_VIEWPORT, which starts the reveal while the
  // block is still just below the screen, so it is already fading in when it
  // scrolls into view instead of sitting blank until 20% of it is visible.
  const reveal = (props) => (revealOn ? { ...props, viewport: REVEAL_VIEWPORT } : {});

  // Animation setup
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.1, // start almost immediately once the section enters
      },
    },
  };

  // 👇 Smooth, no-bounce upward motion
  const smoothFloatUp = {
    hidden: { opacity: 0, y: 80 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1], // ease-out: visible quickly, settles softly
      },
    },
  };



  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // Randomised once per mount (lazy state), not on every render — otherwise
  // each re-render (e.g. a data fetch landing) made all 60 bubbles jump.
  const [bubbles] = useState(() => Array.from({ length: 60 }, (_, i) => {
    const sizes = [20, 30, 40, 50, 60, 70, 80];
    const size = sizes[Math.floor(Math.random() * sizes.length)];
    const isLarge = size > 45;
    const isDark = Math.random() > 0.5;
    return {
      id: i,
      size: size,
      left: Math.random() * 100,
      delay: Math.random() * 20,
      duration: 20 + Math.random() * 15,
      opacity: isLarge ? 0.25 + Math.random() * 0.25 : 0.15 + Math.random() * 0.2,
      drift: (Math.random() - 0.5) * 100,
      color: isDark ? "#006FDF" : "#389BFF",
    };
  }));

  // "Our Track Record So Far" counters — CMS-managed (Admin → Track Record).
  // Seeded with FALLBACK_STATS so the section renders instantly, then replaced
  // with the live values fetched from the API below.
  const [stats, setStats] = useState(FALLBACK_STATS);

  // Hero banner text/CTA — CMS-managed (Admin → Home Hero). Fallback renders
  // instantly; replaced with the live values from the hero API below.
  const [hero, setHero] = useState(FALLBACK_HERO);

  const services = [
    { title: "SAP IBP Solutions", img: Service10, path: serviceTabPath("/digital-core-transformation", "ibp") },
    { title: "SAP S/4 HANA Solutions", img: Service8, path: serviceTabPath("/digital-core-transformation", "sap") },
    { title: "Industry 4.0/5.0 Solutions", img: Service1, path: serviceTabPath("/intelligent-industry-solutions", "industry") },
    { title: "Cloud Transformation Services", img: Service5, path: serviceTabPath("/data-cloud-transformation", "cloud") },
    { title: "Data Engineering", img: Service6, path: serviceTabPath("/data-cloud-transformation", "data") },
    { title: "AI / ML Solutions", img: Service7, path: serviceTabPath("/intelligent-industry-solutions", "aiml") },
    { title: "Product Engineering Services", img: Service2, path: serviceTabPath("/product-engineering-innovation", "product") },
    { title: "Web Development", img: Service3, path: serviceTabPath("/digital-experience-engagement", "web") },
    { title: "App Development", img: Service9, path: serviceTabPath("/digital-experience-engagement", "mobile") },
    { title: "Resource Augmentation Solutions", img: Service4, path: serviceTabPath("/flexible-talent-delivery-models", "staff") },
  ];
  const countries = [
    {
      section: 'left',
      items: [
        {
          flagImg: India, // Replace with: IndiaFlag
          name: 'India',
          cities: ['Mumbai', 'Pune', 'Kolkata', 'Hyderabad', 'Bengaluru']
        }
      ]
    },
    {
      section: 'middle',
      items: [
        { flagImg: Canada, name: 'Canada' }, // Replace with: CanadaFlag
        { flagImg: Singapore, name: 'Singapore' }, // Replace with: SingaporeFlag
        { flagImg: Australia, name: 'Australia' } // Replace with: AustraliaFlag
      ]
    },
    {
      section: 'right-top',
      items: [
        { flagImg: USA, name: 'USA' }, // Replace with: USAFlag
        { flagImg: Europe, name: 'Europe' }, // Replace with: EuropeFlag
        { flagImg: SK, name: 'South Korea' } // Replace with: SouthKoreaFlag
      ]
    },
    {
      section: 'right-bottom',
      items: [
        { flagImg: UK, name: 'UK' }, // Replace with: UKFlag
        { flagImg: Dubai, name: 'Dubai' } // Replace with: DubaiFlag
      ]
    }
  ];

  // "Our In-House Products" cards, the "Empowering Success Stories" client logos
  // and the product category filter are all CMS-managed. `cardImage` uses the
  // product's Website/hero image (per the Figma photo-top card); `categories`
  // drives the filter tabs.
  const API_URL = process.env.REACT_APP_API_URL || "";
  const [products, setProducts] = useState([]);
  // False until the products request settles, so the row shows placeholder
  // cards while loading instead of the "No products" empty message.
  const [productsLoaded, setProductsLoaded] = useState(false);
  const [clients, setClients] = useState([]);
  const [productCategories, setProductCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState("All");

  useEffect(() => {
    let active = true;
    productAPI
      .getCards()
      .then((res) => {
        if (!active) return;
        setProducts(
          (res.data || [])
            // Products that moved to AI Solutions show in "AI That Works for You".
            .filter((p) => !isMovedToAISolutions(p))
            .map((p) => ({
              id: p._id,
              title: p.title,
              description: p.cardDescription,
              image: p.cardLogo ? `${API_URL}${p.cardLogo}` : null,
              // The card is an <img>, so a product whose media is a video shows the
              // video's thumbnail. An uploaded image still wins when both are set.
              cardImage: p.heroImage
                ? `${API_URL}${p.heroImage}`
                : youTubeThumb(p.heroVideoUrl),
              categories: p.categories || [],
              logoClassName: p.logoClassName,
              // Canonical /products/:slug page; the CMS link is often the legacy
              // "/products?tab=…" form, which only renders the generic /products.
              link: p.slug && !p.isExternal ? `/products/${p.slug}` : p.link,
              isExternal: p.isExternal,
            }))
        );
      })
      .catch(() => {})
      .finally(() => {
        if (active) setProductsLoaded(true);
      });
    clientAPI
      .getAll()
      .then((res) => {
        if (active) setClients(res.data || []);
      })
      .catch(() => {});
    statAPI
      .getAll()
      .then((res) => {
        // Only replace the fallback if the API returns at least one stat.
        if (active && Array.isArray(res.data) && res.data.length) {
          setStats(res.data);
        }
      })
      .catch(() => {});
    heroAPI
      .get()
      .then((res) => {
        // Only replace the fallback with a real hero OBJECT (guards against a
        // non-JSON response, e.g. an SPA index.html fallback, blanking the hero).
        if (active && res.data && typeof res.data === "object" && !Array.isArray(res.data)) {
          setHero(res.data);
        }
      })
      .catch(() => {});
    categoryAPI
      .getByType("products")
      .then((res) => {
        if (active) setProductCategories(res.data || []);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [API_URL]);

  // "Insights & Case Studies": the newest approved posts from the CMS (this
  // section used to be three hardcoded placeholder cards). Up to two blogs and
  // the newest case study, newest first; if one type is short, the other fills
  // the row. null = still loading; [] = nothing to show, so the grid is hidden.
  const [insights, setInsights] = useState(null);
  useEffect(() => {
    let active = true;
    Promise.allSettled([
      blogAPI.getAll({ limit: 3, fields: "card" }),
      caseStudyAPI.getAll({ limit: 3, fields: "card" }),
    ]).then(([b, c]) => {
      if (!active) return;
      const listOf = (r, key) =>
        r.status === "fulfilled" && Array.isArray(r.value.data?.[key]) ? r.value.data[key] : [];
      const blogs = listOf(b, "blogs").map((x) => ({ ...x, kind: "blog" }));
      const cases = listOf(c, "caseStudies").map((x) => ({ ...x, kind: "case" }));
      const picked = [...blogs.slice(0, 2), ...cases.slice(0, 1)];
      const rest = [...blogs.slice(2), ...cases.slice(1)];
      while (picked.length < 3 && rest.length) picked.push(rest.shift());
      picked.sort((x, y) => new Date(y.createdAt) - new Date(x.createdAt));
      setInsights(picked);
    });
    return () => {
      active = false;
    };
  }, []);

  // Only surface category tabs that actually have at least one (approved)
  // product. The categories API returns every product category, including ones
  // left empty after their last product was deleted — those used to linger as
  // dead tabs. Deriving the tabs from the live products makes an empty category
  // drop out of the row automatically the moment its last product is removed.
  const usedCategoryNames = new Set(
    products.flatMap((p) => p.categories || [])
  );
  const visibleProductCategories = productCategories.filter((c) =>
    usedCategoryNames.has(c.name)
  );

  // Filter products by the selected category tab ("All" shows everything). If the
  // active category no longer has any products (e.g. it was just emptied), fall
  // back to "All" so we never show an empty, orphaned filter.
  const effectiveCategory =
    activeCategory !== "All" && !usedCategoryNames.has(activeCategory)
      ? "All"
      : activeCategory;
  const filteredProducts =
    effectiveCategory === "All"
      ? products
      : products.filter((p) => (p.categories || []).includes(effectiveCategory));

  // Reset the products row to the start and refresh the arrows whenever the
  // category filter changes or products finish loading; also on window resize.
  useEffect(() => {
    if (productsScrollRef.current) productsScrollRef.current.scrollLeft = 0;
    const t = setTimeout(checkProductsScroll, 60);
    window.addEventListener("resize", checkProductsScroll);
    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", checkProductsScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCategory, products]);

  const aiTools = [
    {
      id: 1,
      title: "BestoFlow",
      description: "Transform manual approvals into seamless, automated workflows right inside SAP IBP Excel.",
      image: BestoFlowImg,
      // Title text sits on the left of this banner, so crop from the right
      imageFit: "left",
      link: "/bestoflow"
    },
    {
      id: 2,
      title: "PredoSolution",
      description: "Predict Failures. Prevent Downtime. Protect Your Revenue.",
      image: PredoImg,
      // Title text sits on the left of this photo, so crop from the right
      imageFit: "left",
      link: "/predosolutions"
    },
    {
      id: 3,
      title: "AskOchat",
      description: "Fast, Reliable IT Help - No Wait Times.",
      image: AskO,
      // Title text sits on the left of this banner, so crop from the right
      imageFit: "left",
      link: "/askochat"
    },
    {
      id: 4,
      title: "Zenetick-AI",
      description: "Transform Content at Scale - Text & Images to Video in Seconds.",
      image: Zentick,
      // Title text sits on the left of this banner, so crop from the right
      imageFit: "left",
      link: "/zentick_ai"
    },
    {
      id: 5,
      title: "Besto Jyotish",
      description: "Intelligent Vedic Astrology, Powered by AI.",
      image: Besto_Jyotish,
      // Title text sits on the left of this banner, so crop from the right
      imageFit: "left",
      link: "/besto_jyotish"
    },

  ];

  return (
    <div className="overflow-x-hidden w-full">
      <Seo
        fullTitle="Bestowal Systems and Services Pvt. Ltd."
        description="Bestowal Systems and Services Pvt. Ltd. delivers custom software development, web & mobile apps, SAP, cloud, AI/ML, RPA and digital transformation services for enterprises across industries."
        path="/"
      />
      {/* A very fast scroll shows the sections it lands on instantly. */}
      <FastScrollReveal />
      {/* Hero Section */}
      {/* The section paints the poster (img2) as its own background with a dark
          brand-colour fallback. Unlike the video wrapper, the section is NOT in
          the GSAP opacity fade, so it shows instantly on cold load — replacing
          the gray empty-<video> box with the poster/dark colour until the video
          decodes and crossfades in over it. Same URL as the <video> poster, so
          it's a single shared download. */}
      <section
        ref={heroRef}
        className="hero-fullscreen relative overflow-hidden text-white w-full min-h-[calc(100svh-160px)] flex items-center justify-start"
        style={{
          backgroundColor: "#15243a",
          backgroundImage: `url(${Img2})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <SeamlessLoopVideo
          ref={heroVideoRef}
          src={Img1}
          poster={Img2}
          className="absolute inset-0 w-full h-full"
          videoClassName="absolute inset-0 w-full h-full object-cover transform -scale-x-100"
        />
        <div ref={heroOverlayRef} className="absolute inset-0 bg-black/50" />

        {/* Animate everything together from left to right */}
        <motion.div
          {...reveal({
            initial: "hidden",
            whileInView: "visible",
            variants: {
              hidden: {},
              visible: { transition: { staggerChildren: 0.15 } },
            },
          })}
          className="relative z-10 px-6 md:px-20 lg:px-32 py-10 flex flex-col items-start gap-[clamp(16px,2vh,24px)]"
        >
          {/* Same left-to-right slide, as a 0.7s ease-out tween instead of the
              soft springs, which took ~3s to settle and kept the CTA button
              hidden until ~1.4s after load. */}
          <motion.h1
            variants={{
              hidden: { x: -200, opacity: 0 },
              visible: { x: 0, opacity: 1, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
            }}
            className="font-bold leading-tight text-white text-[clamp(25px,4.2vw,64px)]"
          >
            {hero.headingLine1}
            {hero.headingLine2 ? <><br /> {hero.headingLine2}</> : null}
          </motion.h1>

          <motion.p
            variants={{
              hidden: { x: -200, opacity: 0 },
              visible: { x: 0, opacity: 1, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
            }}
            className="max-w-[700px] text-[clamp(14px,1.25vw,18px)]"
            style={{ color: "#CFDAE5", fontWeight: 400 }}
          >
            {hero.subheading}
          </motion.p>

          {/* A real <a href> so crawlers can follow it; internal links still
              navigate client-side. */}
          <motion.a
            variants={{
              hidden: { x: -200, opacity: 0 },
              visible: { x: 0, opacity: 1, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
            }}
            href={hero.ctaLink || "/bestoservice"}
            onClick={(e) => {
              const link = hero.ctaLink || "/bestoservice";
              if (/^https?:\/\//.test(link)) return;
              e.preventDefault();
              navigate(link);
            }}
            className="inline-block bg-[#0B72D6] hover:bg-[#1a61d6] text-white px-6 py-3 rounded-none font-medium transition-colors duration-200 text-[clamp(14px,1.05vw,18px)]"
          >
            {hero.ctaText}
          </motion.a>
        </motion.div>
      </section>


      {/* Stats Section */}
      <section className="bg-white px-6 py-16 lg:py-[80px] lg:px-[100px] overflow-hidden">
        <div className="text-center mb-16">
          <h2 className="text-[24px] lg:text-[48px] font-bold text-[#1B354F] mb-4">
            <span className="border-l-[6px] border-orange-500 pl-4">
              Our Track Record So Far
            </span>
          </h2>
          <p className="text-[15px] lg:text-[20px] text-[#5E6C7B] mt-6">
            Delivering exceptional results through strategic innovation and
            <br /> technological excellence.
          </p>
        </div>

        <motion.div
          className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 lg:gap-[24px]"
          {...reveal({
            variants: containerVariants,
            initial: "hidden",
            whileInView: "visible",
          })}
        >
          {stats.map((stat, idx) => {
            // stat.icon is a lucide icon NAME (from the CMS); map it to the
            // component, falling back to Grid3x3 for unknown/empty names.
            const IconComponent = STAT_ICON_MAP[stat.icon] || Grid3x3;
            return (
              <motion.div
                // Key on the (stable) label, NOT _id: the fallback stats have no
                // _id, so keying on _id remounted every card when the API data
                // arrived — and a remount after the whileInView reveal already
                // fired left the fresh cards stuck hidden (blank section). Labels
                // are identical between fallback and DB, so this updates in place.
                key={stat.label || idx}
                variants={smoothFloatUp}
                // Transition only the hover colours: `transition-all` also
                // smoothed framer-motion's per-frame opacity/transform writes,
                // making the reveal lag ~0.3s behind.
                className="group flex flex-col lg:h-[260px] items-center bg-[#EEFAFF] text-center rounded-lg cursor-pointer mx-auto hover:bg-[#0d6efd] hover:shadow-xl transition-[background-color,box-shadow] duration-300"
                style={{ width: "100%" }}
              >
                <div className="mb-6 lg:pt-[32px] transition-colors duration-300">
                  <IconComponent className="w-12 h-12 lg:w-[26px] lg:h-[26px] text-[#0B72D6] group-hover:text-white transition-colors duration-300" />
                </div>
                <h3 className="text-[20px] lg:text-[48px] font-extrabold mb-4 text-[#0B72D6] group-hover:text-white transition-colors duration-300">
                  {stat.number}
                </h3>
                <p className="text-[15px] lg:text-[20px] font-medium leading-snug px-6 text-[#1B354F] group-hover:text-white transition-colors duration-300">
                  {stat.label}
                </p>
              </motion.div>
            );
          })}
        </motion.div>
      </section>

      {/* About Section */}
      {/* min-h on mobile lets the box grow with its content (heading + text +
          stacked icons) so it can't overflow into the next section; desktop
          keeps the exact fixed 680px height. */}
      <div className="relative min-h-[680px] lg:h-[680px] overflow-x-hidden">
        {/* Dark overlay always visible */}
        <div className="absolute inset-0 bg-black/90 z-0"></div>

        {/* Animated image */}
        <motion.div
          {...reveal({
            initial: { x: -200, opacity: 0 },
            whileInView: { x: 0, opacity: 1 },
            transition: { duration: 0.6, ease: "easeOut" },
          })}
          className="absolute inset-0 z-10"
        >
          <img
            src={Img2}
            alt="Team collaboration"
            className="w-full h-full object-cover"
          />
          {/* Optional gradient over image */}
          <div className="absolute inset-0 bg-gradient-to-l from-black/70 via-black/50 to-transparent"></div>
        </motion.div>

        {/* Animated content */}
        <motion.div
          {...reveal({
            initial: { x: 200, opacity: 0 },
            whileInView: { x: 0, opacity: 1 },
            transition: { duration: 0.6, ease: "easeOut", delay: 0.1 },
          })}
          className="relative z-20 w-full px-6 py-12 md:py-16 lg:py-20 flex justify-end"
        >
          <div className="max-w-[798px] lg:pr-[60px]">
            <h2 className="text-[24px] lg:text-[48px] font-bold leading-tight tracking-tight text-white mb-6">
              <span className="border-l-[6px] border-[#F26322] pl-4">
                Who <span>we are</span>
              </span>
            </h2>

            <p className="text-[#BCB9B9] text-[15px] lg:text-[20px] leading-relaxed mb-12 lg:mb-[70px]">
              Bestowal Systems and Services Pvt. Ltd. is a global technology partner delivering
              smart, scalable, and secure digital solutions. With expertise in SAP Transformation,
              Cloud, AI/ML, and Product Engineering, we help enterprises accelerate innovation,
              strengthen compliance, and achieve sustainable growth.
              <br /><br />
              Our 130+ skilled professionals work with governments, corporates, and startups
              worldwide—designing and delivering solutions that enhance efficiency, transparency,
              and resilience. Backed by ISO 27001:2022 certification, we ensure the highest
              standards of data security and trust.
            </p>

            {/* Icons section */}
            <div className="flex flex-col md:flex-row gap-8 lg:gap-[40px] items-center md:items-start">
              {[{ icon: Lightbulb, label: "Innovation\nDriven" }, { icon: Gauge, label: "Results\nOriented" }, { icon: Users, label: "Clients\nFocused" }].map((item, idx) => {
                const Icon = item.icon;
                return (
                  <motion.div
                    key={idx}
                    {...reveal({
                      initial: { y: 50, opacity: 0 },
                      whileInView: { y: 0, opacity: 1 },
                      transition: { duration: 0.6, ease: "easeOut", delay: idx * 0.1 },
                    })}
                    className="flex flex-col items-center text-center"
                  >
                    <div className="w-12 h-12 lg:w-[80px] lg:h-[80px] rounded-full flex items-center justify-center mb-3 lg:mb-[18px]">
                      <Icon className="w-6 h-6 lg:w-[48px] lg:h-[48px] text-[#F26322]" />
                    </div>
                    <p className="text-white text-[12px] lg:text-[16px] font-medium whitespace-pre-line">
                      {item.label}
                    </p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </motion.div>
      </div>


      {/* Services Section */}
      <section className="px-6 lg:px-16 py-12 lg:py-[80px] lg:px-[120px]">
        <div className="text-center mb-12 lg:mb-16">
          <h2 className="text-[24px] lg:text-[48px] font-bold text-[#163365] mb-4">
            <span className="border-l-[6px] border-[#F26322] pl-4">
              Our Core Services
            </span>
          </h2>
          <p className="text-[16px] lg:text-[20px] text-[#5E6C7B] mt-4">
            Comprehensive solutions designed to accelerate your digital transformation journey.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-x-[24px] gap-y-[40px]">
          {services.map((service, index) => (
            <motion.div
              key={index}
              // Only the hover padding transitions (not `all`): a transition on
              // opacity/transform fought framer-motion's reveal and delayed it ~0.5s.
              className={`group bg-[#F5F7FA] rounded-none cursor-pointer relative overflow-hidden lg:transition-[padding] lg:duration-500 p-[9px] lg:hover:p-0 h-[324px] lg:h-[482px] ${index >= 4 ? "max-lg:!hidden" : ""}`}
              {...reveal({
                initial: { opacity: 0, x: -100 },
                whileInView: { opacity: 1, x: 0 },
                // Stagger within the row (desktop grid is 5 wide), not across
                // all 10 cards — a global index made the 10th card wait 1.35s.
                transition: {
                  duration: 0.6,
                  ease: [0.22, 1, 0.36, 1],
                  delay: (index % 5) * 0.08,
                },
              })}
              onClick={() => service.path && navigate(service.path)}
            >
              {/* Image */}
              <div className="mb-4 overflow-hidden rounded-none h-[180px] lg:h-[326px] transition-all duration-500">
                <img
                  src={service.img}
                  alt={service.title}
                  className="object-cover lg:transition-transform lg:duration-500 lg:group-hover:scale-105 w-full h-full"
                />
              </div>

              {/* Content wrapper with padding */}
              <div className="px-4 pb-4 lg:transition-all lg:duration-500 lg:group-hover:px-6 lg:group-hover:pb-6">
                {/* Title */}
                <h3
                  className="text-[#333333] font-medium mb-[14px] h-[44px] lg:mb-[30px] lg:h-[60px] flex items-start text-[18px] lg:text-[24px]"
                  style={{ fontWeight: 500 }}
                >
                  {service.title}
                </h3>

                {/* Button + Arrow wrapper */}
                <div className="relative h-12">
                  {/* Moving Arrow (outside button) */}
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 flex items-center gap-2 lg:transition-all lg:duration-300 lg:group-hover:left-[calc(100%-100px)] z-0">
                    <ArrowRight className="w-[20px] h-[20px] lg:h-[24px] lg:w-[24px] text-[#F26322]" />
                  </div>

                  {/* Learn More Button */}
                  {/* stopPropagation: the card's own onClick would navigate a second time */}
                  <Link
                    to={service.path}
                    onClick={(e) => e.stopPropagation()}
                    className="absolute inset-0 w-full h-full bg-[#3B9EF8] text-white rounded-none flex items-center justify-center gap-2 font-medium text-[14px] lg:text-[16px] shadow-lg transition-all duration-500 opacity-0 z-0 max-lg:!opacity-100 group-hover:!opacity-100 group-hover:z-10 px-[12px] lg:px-[21px] lg:h-[48px]"
                  >
                    <span>
                      Learn More
                    </span>
                    <ArrowRight
                      className="w-[16px] h-[16px] lg:h-[24px] lg:w-[24px] text-white"
                    />
                  </Link>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Mobile only: the homepage shows the first 4 services (the rest are
            hidden via max-lg:!hidden above) and links out to the full Services
            page, so this section stays short on phones. Desktop's 5-col grid
            already shows all 10, so the button is hidden there. */}
        <div className="lg:!hidden mt-10 flex justify-center">
          <Link
            to="/bestoservice"
            className="inline-flex items-center justify-center gap-2 border border-[#0B72D6] text-[#0B72D6] font-medium rounded-none py-3 px-6 text-[16px] transition-colors duration-300 hover:bg-[#0B72D6] hover:text-white"
          >
            View All Services
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>


      {/* Products Section */}
      <div>
        <section className="py-12 px-6 lg:px-[124px] bg-white">
          {/* Animate text from top only when in viewport */}
          <motion.div
            className="text-center mb-10 lg:mb-16"
            {...reveal({
              initial: { y: -50, opacity: 0 },
              whileInView: { y: 0, opacity: 1 },
              transition: { duration: 0.6, ease: "easeOut" },
            })}
          >
            <h2 className="text-[24px] lg:text-[48px] font-bold text-[#2C3E50] mb-4">
              <span className="border-l-[6px] border-[#F26322] pl-4">Our In-House Products</span>
            </h2>
            <p className="text-[16px] lg:text-[20px] text-[#5E6C7B] mt-4">
              We're proud to launch products that empower different sectors
            </p>
          </motion.div>

          {/* Category filter tabs — "All" plus the admin-managed product
              categories. Selecting one filters the carousel below.
              Mobile: a single swipeable row (flex-nowrap + overflow-x-auto) so
              the chips never wrap onto multiple lines; `-mx-6 px-6` lets them
              scroll edge-to-edge within the section's padding, and the scrollbar
              is hidden. Desktop (lg): back to a centered wrap where all chips fit. */}
          <div className="flex flex-nowrap lg:flex-wrap justify-start lg:justify-center gap-3 lg:gap-4 mb-8 lg:mb-12 overflow-x-auto lg:overflow-visible scrollbar-hide -mx-6 px-6 lg:mx-0 lg:px-0">
            {["All", ...visibleProductCategories.map((c) => c.name)].map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`flex-shrink-0 px-4 sm:px-6 py-2.5 rounded-md text-[14px] sm:text-[16px] lg:text-[18px] font-medium whitespace-nowrap transition-all duration-200 ${effectiveCategory === cat
                  ? "bg-[#0B72D6] text-white border border-[#0B72D6] shadow-md"
                  : "border border-[#D6DEE6] text-[#5E6C7B] hover:border-[#0A5DB3] hover:text-[#0A5DB3] hover:bg-[#E6F0FF]"
                  }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Equal-size product cards in a single, horizontally-scrollable row.
              Hovering a card enlarges it and reveals "Learn more". Desktop arrows
              scroll the row; touch/trackpad can swipe it. A unique class
              (`inhouse-card`) avoids the global App.css `.product-card` styles. */}
          {!productsLoaded ? (
            // Placeholder cards (same footprint as the real ones) until the
            // products request settles, so the row doesn't flash "No products"
            // and then jump in height when the cards arrive.
            <div className="overflow-hidden" aria-hidden="true">
              <div className="flex gap-6 lg:gap-8 w-max pt-8 pb-16 px-2">
                {[0, 1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="w-[280px] sm:w-[320px] lg:w-[348px] bg-white border border-[#E6EBF1] rounded-2xl overflow-hidden animate-pulse"
                  >
                    <div className="w-full h-[200px] lg:h-[230px] bg-[#EEF1F5]" />
                    <div className="p-5 lg:p-6">
                      <div className="h-[30px] w-3/5 rounded bg-[#EEF1F5] mb-2" />
                      <div className="h-[18px] w-full rounded bg-[#EEF1F5] mb-2" />
                      <div className="h-[18px] w-4/5 rounded bg-[#EEF1F5]" />
                      <div className="mt-3 h-[24px]" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : filteredProducts.length > 0 ? (
            <div className="relative">
              {/* Left arrow — desktop only */}
              <button
                onClick={() => scrollProducts("left")}
                disabled={!canScrollLeftProd}
                aria-label="Scroll products left"
                className={`absolute max-lg:!hidden left-[-24px] top-1/2 -translate-y-1/2 -translate-x-4 lg:-translate-x-12 z-10 w-10 h-10 lg:w-12 lg:h-12 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 ${canScrollLeftProd ? "hover:bg-gray-50 opacity-100 cursor-pointer" : "opacity-30 cursor-not-allowed"}`}
              >
                <ArrowLeft className="w-5 h-5 lg:h-[24px] lg:w-[24px] text-[#333333]" />
              </button>

              <div
                ref={productsScrollRef}
                onScroll={checkProductsScroll}
                className="overflow-x-auto scrollbar-hide scroll-smooth"
              >
                {/* Generous vertical padding so the hover shadow has room to
                    render inside the scroll box instead of being clipped (or
                    pushing a vertical scrollbar). px stays small — side overflow
                    just rides the horizontal scroll axis. */}
                <div className="flex gap-6 lg:gap-8 w-max pt-8 pb-16 px-2">
                  {filteredProducts.map((product, idx) => (
                    // Outer wrapper owns the "enter into view" animation. The
                    // entrance must live here, NOT on `.inhouse-card`, because
                    // framer-motion writes an inline `transform` that would
                    // override the CSS `.inhouse-card:hover` scale and break the
                    // hover-enlarge. `reveal()` drops the props on mobile /
                    // reduced-motion so cards show immediately there.
                    // NOTE: fade + scale only — NO vertical (y) translate. This
                    // row is `overflow-x-auto`, which CSS computes to
                    // `overflow-y: auto`, so a downward translate (or an
                    // unclipped hover shadow) overflows vertically and creates a
                    // stray vertical scroll. Scale stays <=1, so it never
                    // overflows the row's bounds.
                    <motion.div
                      key={product.id}
                      className="flex-shrink-0"
                      {...reveal({
                        initial: { opacity: 0, scale: 0.96 },
                        whileInView: { opacity: 1, scale: 1 },
                        // Only the cards visible on arrival stagger; cards
                        // brought in later by sideways scrolling show at once.
                        transition: {
                          duration: 0.5,
                          ease: "easeOut",
                          delay: idx < 5 ? idx * 0.08 : 0,
                        },
                      })}
                    >
                    <div
                      className="inhouse-card relative w-[280px] sm:w-[320px] lg:w-[348px] bg-white border border-[#E6EBF1] rounded-2xl overflow-hidden shadow-[0_2px_8px_rgba(27,53,79,0.05)]"
                    >
                      <div className="w-full h-[200px] lg:h-[230px] overflow-hidden bg-[#F5F7FA]">
                        {product.cardImage ? (
                          <img src={product.cardImage} alt={product.title} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <span className="text-[22px] font-bold text-[#0B72D6] text-center px-3">{product.title}</span>
                          </div>
                        )}
                      </div>
                      <div className="p-5 lg:p-6">
                        <h3 className="text-[20px] lg:text-[24px] font-bold text-[#1B354F] mb-2 leading-tight">{product.title}</h3>
                        <p className="inhouse-desc text-[14px] lg:text-[16px] text-[#5E6C7B] leading-snug">{product.description}</p>
                        {/* Revealed when the card is hovered (the "big" state). */}
                        <div className="inhouse-learn mt-3">
                          {product.isExternal ? (
                            <a
                              href={product.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center text-[#0B72D6] font-semibold text-[14px] lg:text-[16px] hover:text-[#1a75cc] transition-colors"
                            >
                              Learn more
                              <ArrowRight className="inhouse-arrow ml-2 w-5 h-5 transition-transform duration-300" />
                            </a>
                          ) : (
                            <Link
                              to={product.link}
                              className="inline-flex items-center text-[#0B72D6] font-semibold text-[14px] lg:text-[16px] hover:text-[#1a75cc] transition-colors cursor-pointer"
                            >
                              Learn more
                              <ArrowRight className="inhouse-arrow ml-2 w-5 h-5 transition-transform duration-300" />
                            </Link>
                          )}
                        </div>
                      </div>
                    </div>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Right arrow — desktop only */}
              <button
                onClick={() => scrollProducts("right")}
                disabled={!canScrollRightProd}
                aria-label="Scroll products right"
                className={`absolute max-lg:!hidden right-[-24px] top-1/2 -translate-y-1/2 translate-x-4 lg:translate-x-12 z-10 w-10 h-10 lg:w-12 lg:h-12 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 ${canScrollRightProd ? "hover:bg-gray-50 opacity-100 cursor-pointer" : "opacity-30 cursor-not-allowed"}`}
              >
                <ArrowRight className="ml-2 w-5 h-5 lg:h-[24px] lg:w-[24px] text-[#666666] transform group-hover:translate-x-1 transition-transform duration-300" />
              </button>

              <style>{`
                .inhouse-card { transition: transform .35s ease, box-shadow .35s ease, border-color .35s ease; }
                .inhouse-card:hover { transform: scale(1.06); z-index: 10; box-shadow: 0 10px 24px rgba(27,53,79,0.10); border-color: rgba(11,114,214,0.4); }
                .inhouse-card .inhouse-learn { opacity: 0; transition: opacity .3s ease; }
                .inhouse-card:hover .inhouse-learn { opacity: 1; }
                .inhouse-card:hover .inhouse-arrow { transform: translateX(4px); }
                .inhouse-desc { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
              `}</style>
            </div>
          ) : (
            <p className="text-center text-[#5E6C7B] text-[16px] lg:text-[18px] py-10">No products in this category yet.</p>
          )}
        </section>
      </div>



      {/* AI Tools Section - Scrollable Carousel with Arrows */}
      <section className="py-12 px-6 lg:py-[80px] lg:px-[124px] bg-white overflow-hidden">
        <motion.div
          className="text-center mb-10 lg:mb-16"
          {...reveal({
            initial: { opacity: 0, y: 30 },
            whileInView: { opacity: 1, y: 0 },
            transition: { duration: 0.6, ease: "easeOut" },
          })}
        >
          <h2 className="text-[24px] lg:text-[48px] font-bold mb-4">
            <span className="border-l-[6px] border-[#F26322] pl-4">
              <span className="text-[#2C3E50]">AI That Works</span>{" "}
              <span className="text-[#2C3E50]">for You</span>
            </span>
          </h2>
          <p className="text-[16px] lg:text-[20px] text-[#5E6C7B] mt-4">
            We build AI tools that automate, predict, and optimize — turning data
            into actionable intelligence.
          </p>
        </motion.div>

        <div className="relative">
          {/* Left Scroll Button */}
          <button
            className={`absolute max-lg:!hidden left-[-24px] top-1/2 -translate-y-1/2 -translate-x-4 lg:-translate-x-12 z-10 w-10 h-10 lg:w-12 lg:h-12 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 ${canScrollLeftAI ? "hover:bg-gray-50 opacity-100 cursor-pointer" : "opacity-30 cursor-not-allowed"
              }`}
            onClick={() => scrollAI("left")}
            disabled={!canScrollLeftAI}
            aria-label="Scroll left"
          >
            <ArrowLeft className="w-5 h-5 lg:h-[24px] lg:w-[24px] text-[#333333]" />
          </button>

          {/* Carousel Scroll Area */}
          <div ref={aiScrollRef} className="overflow-x-auto overflow-y-hidden scrollbar-hide scroll-smooth snap-x snap-mandatory" onScroll={checkScrollPositionAI}>
            {/* Stagger container: reveals every card (incl. off-screen ones) when the
                section enters view, rather than gating each card on horizontal scroll */}
            <motion.div
              className="flex max-lg:!flex-col gap-x-[24px] gap-y-[24px] lg:gap-y-[40px] pb-4"
              {...reveal({
                initial: "hidden",
                whileInView: "visible",
                variants: {
                  hidden: {},
                  visible: { transition: { staggerChildren: 0.1 } },
                },
              })}
            >
              {aiTools.map((tool, index) => (
                <motion.div
                  key={tool.id}
                  custom={index - 1}
                  className={`ai-tool-card snap-start flex-shrink-0 w-[280px] sm:w-[300px] max-lg:!w-full lg:w-[calc(33.333%-16px)] group bg-[#F5F7FA] rounded-none overflow-hidden flex flex-col p-[16px] ${index >= 4 ? "max-lg:!hidden" : ""}`}
                  variants={{
                    // Slide each card out from the center on reveal: cards left of the
                    // middle enter from the right (then move left), cards to the right
                    // enter from the left (then move right). Pivot = middle of the 3
                    // desktop-visible cards (index 1), so `custom` = index - 1.
                    hidden: (dir) => ({ opacity: 0, x: dir * -100, scale: 0.96 }),
                    visible: { opacity: 1, x: 0, scale: 1 },
                  }}
                  transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                  whileHover={{ y: -8 }}
                >
                  <motion.div
                    className="relative h-[200px] sm:h-[240px] lg:h-[320px] overflow-hidden"
                    transition={{ duration: 0.4 }}
                  >
                    <img
                      src={tool.image}
                      alt={tool.title}
                      className={`w-full h-full ${tool.imageFit === "contain" ? "object-contain bg-[#060D24]" : tool.imageFit === "left" ? "object-cover object-left" : "object-cover"}`}
                    />
                  </motion.div>

                  <div className="p-6 lg:p-8 flex flex-col flex-grow bg-[#F8F9FA]">
                    <h3 className="text-[24px] lg:text-[24px] font-bold text-[#1B354F] mb-3">
                      {tool.title}
                    </h3>
                    <p className="text-[14px] lg:text-[20px] text-[#1B354F] mb-6 flex-grow">
                      {tool.description}
                    </p>

                    <div className="flex justify-center">
                      {/* Client-side <Link> (a plain href reloaded the whole
                          app). aiTools links are all internal routes; an
                          external one would need a plain motion.a again. */}
                      <MotionLink
                        to={tool.link}
                        onClick={scrollToTop}
                        className="inline-flex items-center justify-center gap-2 py-3 px-6 border-1 border-[#0B72D6] text-[#0B72D6] font-semibold text-[14px] lg:text-[16px] rounded-none transition-all duration-300 group-hover:bg-[#0B72D6] group-hover:text-white w-full"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        Read More<span className="visually-hidden"> about {tool.title}</span>
                        <ArrowRight className="w-5 h-5 lg:h-[24px] lg:w-[24px] transform group-hover:translate-x-1 transition-transform duration-300" />
                      </MotionLink>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>

          {/* Right Scroll Button */}
          <button
            className={`absolute max-lg:!hidden right-[-24px] top-1/2 -translate-y-1/2 translate-x-4 lg:translate-x-12 z-10 w-10 h-10 lg:w-12 lg:h-12 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 ${canScrollRightAI ? "hover:bg-gray-50 opacity-100 cursor-pointer" : "opacity-30 cursor-not-allowed"
              }`}
            onClick={() => scrollAI("right")}
            disabled={!canScrollRightAI}
            aria-label="Scroll right"
          >
            <ArrowRight className="ml-2 w-5 h-5 lg:h-[24px] lg:w-[24px] text-[#666666] transform group-hover:translate-x-1 transition-transform duration-300" />
          </button>
        </div>

        {/* Mobile only: stacked vertically, a long AI-tools list gets unwieldy.
            Show the first 4 and link to the full AI Solutions page; the button
            only appears once the list outgrows the limit. Desktop uses the
            horizontal carousel (arrows) and shows everything, so it's hidden there. */}
        {aiTools.length > 4 && (
          <div className="lg:!hidden mt-10 flex justify-center">
            <Link
              to="/AI-Machine-Learning-Solution"
              className="inline-flex items-center justify-center gap-2 border border-[#0B72D6] text-[#0B72D6] font-medium rounded-none py-3 px-6 text-[16px] transition-colors duration-300 hover:bg-[#0B72D6] hover:text-white"
            >
              View All AI Tools
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        )}
      </section>


      {/* Clients Map Section */}
      {/* <div className="w-full flex flex-col items-center justify-center sm:py-12 lg:mt-[173px]">
        <h2 className="text-[20px] mt-5 lg:text-[48px] text-[#163365] font-bold text-center">
          TRUSTED BY{" "}
          <span className="text-orange-500">GLOBAL ORGANIZATION</span>
        </h2>
        <h2 className="text-gray-600 text-center text-[15px] lg:text-[24px] text-[#163365] mt-5 text-[18px] lg:mt-[20px]">Empowering Success Stories: Meet Our Valued Clients</h2>
        <div
          className="relative w-[1000px] max-w-full h-[550px] bg-no-repeat bg-contain bg-center sm:mt-10 lg:mt-[57px] overflow-visible"
          style={{ backgroundImage: `url(${Clients})` }}
        >
          {locations.map((loc, i) => (
            <div
              key={i}
              className="absolute group"
              style={{
                top: loc.top,
                left: loc.left,
                transform: "translate(-50%, -50%)",
              }}
            >
              <div className="sm:w-6 sm:h-6 lg:w-[20px] lg:h-[20px] bg-orange-500 rounded-full border-4 border-white cursor-pointer shadow-lg relative z-20"></div>
              <div
                className="absolute bottom-10 left-1/2 -translate-x-1/2 bg-black text-white text-[13px] md:text-[15px] lg:text-[14px] font-semibold px-3 py-2 rounded-lg lg:opacity-0 group-hover:opacity-100 transition duration-300 whitespace-nowrap z-30"
              >
                {loc.name}
              </div>
            </div>
          ))}
        </div>
      </div> */}
      <div className="w-full lg:py-[80px] lg:px-[100px] px-[10px] py-12 bg-white overflow-hidden">
        {/* ===== Header ===== */}
        <motion.div
          className="flex items-center justify-center mb-12 lg:mb-[44px]"
          {...reveal({
            initial: { opacity: 0, y: -60 },
            whileInView: { opacity: 1, y: 0 },
            transition: { duration: 0.6, ease: "easeOut" },
          })}
        >
          {/* h2: the hero heading is the page's only h1. mb-0 w-auto cancel the global h2 rules so it looks exactly like the old h1. */}
          <h2 className="text-[24px] lg:text-[48px] font-bold text-[#2C3E50] text-center mb-0 w-auto">
            <span className="border-l-[6px] border-orange-500 pl-4">

              Trusted By Global Organization
            </span>
          </h2>
        </motion.div>

        {/* ===== Main Container (desktop: 4 designed columns) =====
            Hidden < lg — the unequal-height columns wrap raggedly on mobile, so
            a dedicated structured layout is rendered below for small screens. */}
        <div className="hidden lg:!flex flex-wrap justify-center gap-x-[28px] gap-y-[20px] lg:gap-x-[40px] lg:gap-y-[24px]">

          {/* =========== India Section =========== */}
          <motion.div
            className="flex flex-col space-y-4 lg:w-[260px]"
            {...reveal({
              initial: { opacity: 0, x: -120 },
              whileInView: { opacity: 1, x: 0 },
              transition: { duration: 0.6, ease: "easeOut" },
            })}
          >
            <div className="flex items-center space-x-3 lg:w-[260px]">
              <img
                src={countries[0].items[0].flagImg}
                alt={countries[0].items[0].name}
                className="w-8 h-8 lg:w-[40px] lg:h-[40px] rounded-full object-cover"
              />
              <span className="text-[18px] lg:text-[20px] font-semibold text-[#2C3E50]">
                {countries[0].items[0].name}
              </span>
            </div>
            <div className="space-y-2 lg:w-[260px]">
              {countries[0].items[0].cities.map((city, idx) => (
                <motion.div
                  key={idx}
                  className="text-[15px] lg:text-[20px] text-[#5E6C7B]"
                  {...reveal({
                    initial: { opacity: 0, x: -50 },
                    whileInView: { opacity: 1, x: 0 },
                    transition: {
                      duration: 0.5,
                      delay: idx * 0.08,
                      ease: "easeOut",
                    },
                  })}
                >
                  {city}
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* =========== Other Country Columns =========== */}
          {[1, 2, 3].map((sectionIndex) => (
            <motion.div
              key={sectionIndex}
              className="flex flex-col lg:w-[260px]"
              {...reveal({
                initial: { opacity: 0, x: 120 },
                whileInView: { opacity: 1, x: 0 },
                transition: {
                  duration: 0.6,
                  delay: sectionIndex * 0.1,
                  ease: "easeOut",
                },
              })}
            >
              {countries[sectionIndex].items.map((country, idx) => (
                <motion.div
                  key={idx}
                  className="flex items-center space-x-3 mb-[20px] lg:mb-[40px]"
                  {...reveal({
                    initial: { opacity: 0, x: 80 },
                    whileInView: { opacity: 1, x: 0 },
                    transition: {
                      duration: 0.5,
                      delay: 0.1 + idx * 0.08,
                      ease: "easeOut",
                    },
                  })}
                >
                  <img
                    src={country.flagImg}
                    alt={country.name}
                    className="w-8 h-8 lg:w-[40px] lg:h-[40px] rounded-full object-cover"
                  />
                  <span className="text-[15px] lg:text-[20px] font-medium lg:font-semibold text-gray-700 lg:text-[#2C3E50] whitespace-nowrap">
                    {country.name}
                  </span>
                </motion.div>
              ))}
            </motion.div>
          ))}
        </div>

        {/* ===== Mobile layout (< lg) — structured: India + cities on top,
            then the remaining countries in a tidy 2-column grid. Static (no
            scroll-reveal) so nothing can stay hidden on touch devices. ===== */}
        <div className="lg:!hidden max-w-[420px] mx-auto">
          {/* India + its cities */}
          <div className="flex flex-col items-center text-center">
            <div className="flex items-center gap-3">
              <img
                src={countries[0].items[0].flagImg}
                alt={countries[0].items[0].name}
                className="w-9 h-9 rounded-full object-cover"
              />
              <span className="text-[18px] font-semibold text-[#2C3E50]">
                {countries[0].items[0].name}
              </span>
            </div>
            <p className="mt-2 max-w-[300px] text-[14px] leading-relaxed text-[#5E6C7B]">
              {countries[0].items[0].cities.join(', ')}
            </p>
          </div>

          {/* divider */}
          <div className="my-6 h-px w-full bg-gray-200" />

          {/* Remaining countries — even 2-column grid */}
          <div className="grid grid-cols-2 gap-x-4 gap-y-5">
            {countries.slice(1).flatMap((c) => c.items).map((country, idx) => (
              <div key={idx} className="flex items-center gap-3">
                <img
                  src={country.flagImg}
                  alt={country.name}
                  className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                />
                <span className="text-[15px] font-medium text-gray-700">
                  {country.name}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Clients Logo Marquee */}
      <div className="bg-[#FEF5EE] py-8 lg:py-[46px]">
        <p className="text-center text-[16px] lg:text-[20px] text-[#163365] font-semibold px-4 mb-6 lg:mb-[40px]">
          Empowering Success Stories: Meet Our Valued Clients
        </p>

        {/* Logo marquee — logos sit directly on the cream section background
            (no white tile/card). The server normalizes every logo onto a uniform
            690×330 TRANSPARENT canvas (the card's ~2:1 shape, object-contain) and
            auto-removes any solid white background, so each logo blends cleanly
            into the cream and they all share an identical frame regardless of the
            logo's shape or source background. */}
        <div className="overflow-hidden">
          <div className="flex animate-client-scroll whitespace-nowrap">
            {clients.map((client, index) => (
              <div
                key={index}
                className="mx-4 flex-shrink-0 w-[230px] h-[80px] flex items-center justify-center overflow-hidden bg-white rounded-xl shadow-md py-[3px]"
              >
                <img
                  src={`${API_URL}${client.logo}`}
                  alt={client.name}
                  className="w-full h-full object-cover"
                  style={{ transform: `scale(${client.scale || 1})` }}
                />
              </div>
            ))}
            {/* duplicate for seamless loop */}
            {clients.map((client, index) => (
              <div
                key={`dup-${index}`}
                className="mx-4 flex-shrink-0 w-[230px] h-[80px] flex items-center justify-center overflow-hidden bg-white rounded-xl shadow-md py-[3px]"
              >
                <img
                  src={`${API_URL}${client.logo}`}
                  alt={client.name}
                  className="w-full h-full object-cover"
                  style={{ transform: `scale(${client.scale || 1})` }}
                />
              </div>
            ))}
          </div>
        </div>

        <style>
          {`
     @keyframes client-scroll {
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-50%);
  }
}

.animate-client-scroll {
  animation: client-scroll 30s linear infinite;
  display: flex;
  width: max-content;
}

/* Pause animation on hover */
.animate-client-scroll:hover {
  animation-play-state: paused;
}
    `}
        </style>
      </div>


      {/* CTA Section */}
      <div className="w-full relative lg:mt-[80px]">
        <div className="relative overflow-hidden bg-[#0B72D6] py-16 px-4 lg:py-[75px]">
          {/* Bubbles */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
            {bubbles.map((bubble) => (
              <div
                key={bubble.id}
                className="absolute rounded-full"
                style={{
                  width: `${bubble.size}px`,
                  height: `${bubble.size}px`,
                  left: `${bubble.left}%`,
                  bottom: "-100px",
                  backgroundColor: bubble.color,
                  opacity: bubble.opacity,
                  animation: `smoothFloatUp ${bubble.duration}s ease-in-out infinite`,
                  animationDelay: `${bubble.delay}s`,
                  boxShadow: `0 0 20px ${bubble.color}`,
                  "--drift": `${bubble.drift}px`,
                }}
              />
            ))}
          </div>

          {/* Content */}
          <div className="relative z-10 mx-auto text-center">
            <h2 className="text-white text-[26px] lg:text-[36px] font-semibold mb-5 lg:mb-8 px-4">
              Ready to Take the Next Step in Your Digital Journey?
            </h2>
            <p className="text-white text-[15px] lg:text-[20px] font-medium mb-8 px-4 mx-auto leading-relaxed">
              Whether you're looking to automate operations, build custom software, adopt AI, or digitally transform your business.
              Bestowal is here to lead you every step of the way.
            </p>
            <div className="flex flex-col sm:flex-row gap-[12px] justify-center items-center px-4 lg:gap-[24px]">
              <Link
                to="/contact-us"
                className="group bg-white text-[#0B72D6] px-9 py-[16px] rounded-none flex items-center justify-center gap-2 text-[16px] font-medium hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 w-full sm:w-auto lg:w-[354px]"
              >
                Start Your Transformation Journey
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform lg:h-[24px] lg:w-[24px]" />
              </Link>

              <Link
                to="/contact-us"
                className="group bg-transparent border border-white text-white px-9 py-[15px] rounded-none flex items-center gap-2 text-[16px] font-medium hover:bg-white hover:text-[#0B72D6] transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 w-full sm:w-auto lg:w-[354px] justify-center"
              >
                <MessageCircle className="w-5 h-5 lg:h-[24px] lg:w-[24px]" />
                Talk To Our Experts
              </Link>
            </div>
          </div>
        </div>

        {/* Keyframes */}
        <style>
          {`
          @keyframes smoothFloatUp {
            0% {
              transform: translateX(0) translateY(0);
              opacity: 0;
            }
            50% {
              opacity: 1;
            }
            100% {
              transform: translateX(var(--drift)) translateY(-120vh);
              opacity: 0;
            }
          }
        `}
        </style>
      </div>

      {/* Blog Section */}
      <div className="py-16 lg:px-[100px] lg:py-[80px]">
        <motion.div
          className="text-center mb-12"
          {...reveal({
            initial: { opacity: 0, y: 30 },
            whileInView: { opacity: 1, y: 0 },
            transition: { duration: 0.6, ease: "easeOut" },
          })}
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4 lg:text-[48px] lg:mb-[20px]">
            <span className="text-[#F26322]">Insights & Case</span> <span className="text-[#163365]">Studies</span>
          </h2>
          <p className="text-[15px] text-[#1e3a8a] mx-auto lg:text-[24px] leading-relaxed">
            Explore how we solve real-world challenges using technology. Stay updated with best practices, digital trends, and client success stories.
          </p>
        </motion.div>
        <div className="mx-auto">
          {/* Placeholders while loading keep the section's height steady. The
              grid mounts once the posts arrive, so its stagger reveal plays
              with the real cards. Nothing to show → only the button remains. */}
          {insights === null ? (
            <div className="px-[10px] grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" aria-hidden="true">
              {[0, 1, 2].map((i) => (
                <div key={i} className="bg-[#F5F7FA] animate-pulse">
                  <div className="aspect-[16/9] bg-gray-200" />
                  <div className="p-6 lg:pt-[31px] lg:px-[24px] space-y-4">
                    <div className="h-6 w-24 bg-gray-200 rounded" />
                    <div className="h-6 w-full bg-gray-200 rounded" />
                    <div className="h-5 w-1/2 bg-gray-200 rounded" />
                  </div>
                </div>
              ))}
            </div>
          ) : insights.length > 0 && (
          /* Stagger container: cards fade up in sequence when the grid enters view */
          <motion.div
            className="px-[10px] grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            {...reveal({
              initial: "hidden",
              whileInView: "visible",
              variants: {
                hidden: {},
                visible: { transition: { staggerChildren: 0.15 } },
              },
            })}
          >
            {insights.map((item) => {
              const isCase = item.kind === "case";
              const href = isCase ? `/case-study/${item.slug}` : `/blogs/${item.slug}`;
              const img = item.thumbnail || item.image;
              const fallback = isCase ? casestudyImg : blogsimg;
              return (
                <motion.div
                  key={item._id}
                  className="bg-white rounded-none overflow-hidden flex flex-col"
                  variants={{
                    hidden: { opacity: 0, y: 60 },
                    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
                  }}
                >
                  <Link to={href} onClick={scrollToTop} tabIndex={-1} aria-hidden="true" className="block aspect-[16/9] overflow-hidden bg-gray-100">
                    <img
                      {...(img
                        ? responsiveImg(img, { sizes: "(min-width: 1024px) 30vw, (min-width: 768px) 48vw, 100vw" })
                        : { src: fallback })}
                      alt=""
                      width={800}
                      height={450}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover"
                      onError={imgErrorFallback(img, fallback)}
                    />
                  </Link>
                  <div className="p-6 lg:pt-[31px] lg:px-[24px] bg-[#F5F7FA] flex-grow flex flex-col">
                    <div className="flex justify-between items-center gap-2 flex-wrap sm:mb-3 lg:mb-[16px]">
                      <span
                        className={`text-[14px] lg:text-[16px] font-medium px-[12px] py-[4px] rounded ${
                          isCase ? "text-[#0A5DB3] bg-[#D8F3FF]" : "text-[#d35400] bg-[#fee9dc]"
                        }`}
                      >
                        {isCase ? "Case Study" : "Blog"}
                      </span>
                      <span className="text-[14px] lg:text-[15px] text-[#6B7280]">
                        {new Date(item.createdAt).toLocaleDateString("en-US", {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                        })}
                      </span>
                    </div>
                    <h3 className="text-[18px] font-regular text-[#2c3e50] sm:mb-4 lg:mb-[16px] lg:text-[24px] clamp-2-lines">
                      <Link to={href} onClick={scrollToTop} className="hover:text-[#0B72D6]">
                        {item.title}
                      </Link>
                    </h3>
                    {item.author?.name && (
                      <p className="text-[14px] lg:text-[18px] text-[#2c3e50]">By {item.author.name}</p>
                    )}
                    <div className="mt-auto pt-6 flex justify-center">
                      <Link
                        to={href}
                        className="inline-flex items-center text-[#0B72D6] font-medium hover:underline text-[16px]"
                        onClick={scrollToTop}
                      >
                        Read More<span className="visually-hidden"> about {item.title}</span>
                        <ArrowRight className="ml-2 w-5 h-5" />
                      </Link>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
          )}
          <div className="text-center mt-[40px]">
            <Link
              to="/blogs"
              className="inline-flex items-center justify-center lg:py-[14px] gap-2 border-1 border-[#0B72D6] text-[#0B72D6] font-medium rounded-none py-3 px-6 text-[16px] transition-all duration-300 hover:bg-[#0B72D6] hover:text-white"
            >
              View All Blogs & Case Studies
              <ArrowRight className="w-5 h-5 lg:h-[24px] lg:w-[24px] transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </div>

      <style>{`
    .marquee-container {
      width: 100%;
      overflow: hidden;
      position: relative;
    }
    
    .marquee-content {
      display: flex;
      animation: scroll 10s linear infinite; /* Faster speed - 20 seconds */
      will-change: transform;
    }
    
    .marquee-content:hover {
      animation-play-state: paused;
    }
    
    @keyframes scroll {
      0% {
        transform: translateX(0);
      }
      100% {
        transform: translateX(-50%);
      }
    }
  `}</style>
    </div>
  );
};

export default Hero;